import { useCallback } from "react";
import { jsDateToDateTime, dateTimeRecordToLocalisedDateTime, dateTimeToDateTimeRecord } from "@visioni/schedules";
import type { AlarmOverride } from "@src/services/api/types";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";
import { getScheduleFromAlarmDetail, getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { useUpdateAlarmOverride } from "@src/services/api/hooks";
import { showErrorToast } from "@src/utils/toast";
import type { AlarmException } from "@visioni/a3s-alarm-schedules/app";
import type { OverrideMode } from "./useAlarmOverridePanelState";

interface UseSubmitAlarmOverrideOptions {
  mode: OverrideMode;
  alarmDetail?: AlarmConfiguration | null;
  cameraKey?: string;
  siteId: string;
  requireSchedule?: boolean;
  onSuccess?: (context: { mode: OverrideMode; override: AlarmOverride }) => void;
  onError?: (error: unknown) => void;
}

export function useSubmitAlarmOverride({
  mode,
  alarmDetail,
  cameraKey,
  siteId,
  requireSchedule = false,
  onSuccess,
  onError,
}: UseSubmitAlarmOverrideOptions) {
  const updateOverrideMutation = useUpdateAlarmOverride();

  const submitOverride = useCallback(
    (exception: AlarmException) => {
      if (!alarmDetail || !cameraKey) return;
      const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";

      if (requireSchedule && !getScheduleFromAlarmDetail(alarmDetail)) {
        showErrorToast({ message: "Error", description: "No schedule found for this camera" });
        return;
      }

      const exceptionStartDate = new Date(
        exception.period.start.year,
        exception.period.start.month - 1,
        exception.period.start.day,
        exception.period.start.hour,
        exception.period.start.minute,
      );
      const exceptionEndDate = new Date(
        exception.period.end.year,
        exception.period.end.month - 1,
        exception.period.end.day,
        exception.period.end.hour,
        exception.period.end.minute,
      );
      const durationMs = exceptionEndDate.getTime() - exceptionStartDate.getTime();

      const existingOverride = alarmDetail.settings?.override;
      const existingOverrideEnd = existingOverride?.period.end;

      let startDateTime;
      let endDateTime;

      if (mode === "adjust" && existingOverride && existingOverrideEnd) {
        startDateTime = dateTimeRecordToLocalisedDateTime(existingOverride.period.start, timezone);
        const currentEndDateTime = dateTimeRecordToLocalisedDateTime(existingOverrideEnd, timezone);
        endDateTime = currentEndDateTime.plus({ milliseconds: durationMs });
      } else {
        startDateTime = jsDateToDateTime(exceptionStartDate).setZone(timezone);
        endDateTime = jsDateToDateTime(exceptionEndDate).setZone(timezone);
      }

      const overrideAction =
        mode === "adjust" && existingOverride?.period.end ? existingOverride.action : exception.action;
      const override: AlarmOverride = {
        id: mode === "adjust" && existingOverride?.period.end ? existingOverride.id : exception.id,
        reason: exception.reason,
        action: overrideAction,
        author: null,
        period: {
          start: dateTimeToDateTimeRecord(startDateTime),
          end: dateTimeToDateTimeRecord(endDateTime),
        },
      };

      updateOverrideMutation.mutate(
        // `Alarm Override Saved` is tracked by the mutation, for web and native alike. Only an
        // adjust of an override with an end extends it; any other submit creates one.
        {
          siteId,
          cameraKey,
          override,
          timezone,
          action: mode === "adjust" && existingOverrideEnd ? "extended" : "created",
        },
        {
          onSuccess: () => onSuccess?.({ mode, override }),
          onError: (error) => {
            showErrorToast({ error });
            console.error("Override mutation error:", error);
            onError?.(error);
          },
        },
      );
    },
    [alarmDetail, cameraKey, mode, onError, onSuccess, requireSchedule, siteId, updateOverrideMutation],
  );

  return {
    submitOverride,
    isPending: updateOverrideMutation.isPending,
  };
}
